import { ButtonHTMLAttributes, createContext, HTMLAttributes, useContext, useMemo } from 'react';

import { cn } from './cn';

type ToggleGroupContextValue = {
  isPressed: (value: string) => boolean;
  toggle: (value: string) => void;
};

const ToggleGroupContext = createContext<ToggleGroupContextValue | null>(null);

type ToggleGroupBaseProps = Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'onChange'>;

export type ToggleGroupProps = ToggleGroupBaseProps &
  (
    | {
        type: 'single';
        value: string | null;
        onValueChange: (value: string | null) => void;
      }
    | {
        type: 'multiple';
        value: string[];
        onValueChange: (value: string[]) => void;
      }
  );

export const ToggleGroup = ({ className, type, value, onValueChange, ...props }: ToggleGroupProps) => {
  const contextValue = useMemo<ToggleGroupContextValue>(
    () =>
      type === 'single'
        ? {
            isPressed: itemValue => value === itemValue,
            toggle: itemValue =>
              (onValueChange as (value: string | null) => void)(value === itemValue ? null : itemValue),
          }
        : {
            isPressed: itemValue => (value as string[]).includes(itemValue),
            toggle: itemValue =>
              (onValueChange as (value: string[]) => void)(
                (value as string[]).includes(itemValue)
                  ? (value as string[]).filter(current => current !== itemValue)
                  : [...(value as string[]), itemValue],
              ),
          },
    [type, value, onValueChange],
  );

  return (
    <ToggleGroupContext.Provider value={contextValue}>
      <div
        role="group"
        className={cn('inline-flex items-center overflow-hidden rounded-auditor border border-line-strong', className)}
        {...props}
      />
    </ToggleGroupContext.Provider>
  );
};

export interface ToggleGroupItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string;
}

export const ToggleGroupItem = ({ className, onClick, type = 'button', value, ...props }: ToggleGroupItemProps) => {
  const context = useContext(ToggleGroupContext);
  const pressed = !!context?.isPressed(value);
  return (
    <button
      type={type}
      aria-pressed={pressed}
      data-state={pressed ? 'on' : 'off'}
      className={cn(
        'inline-flex h-8 items-center justify-center gap-1.5 border-r border-line-strong bg-surface px-3 text-xs font-medium',
        'text-ink-muted transition-colors last:border-r-0 hover:bg-canvas hover:text-ink',
        'focus-visible:relative focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=on]:bg-brand-subtle data-[state=on]:text-brand',
        className,
      )}
      onClick={event => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          context?.toggle(value);
        }
      }}
      {...props}
    />
  );
};
